import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AiResponseDetailDto,
  AiResponseDetailDtoCurrentUserFeedbackEnum,
  AiResponseFeedbackInputDtoValueEnum,
  AiResponseSummaryDto,
} from '@kortxio/hub-api';
import { parseDownloadFilename } from 'features/ai-responses/helper';
import { setError } from 'features/error/slice';
import api from 'libs/api';
import { parseApiPayload, toApiError } from 'libs/api/helper';
import { downloadBlob } from 'libs/media';
import { AsyncThunkConfig } from 'store/types';

export type GetAiResponseSummariesArgs = {
  fromDate?: string;
  toDate?: string;
};

export const getAiResponseSummaries = createAsyncThunk<
  AiResponseSummaryDto[],
  GetAiResponseSummariesArgs,
  AsyncThunkConfig
>(
  'aiResponses/getAiResponseSummaries',
  async ({ fromDate, toDate }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.aiResponse.getAiResponseSummariesByParams(
        undefined,
        undefined,
        undefined,
        undefined,
        fromDate,
        toDate
      );

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const getAiResponseDetail = createAsyncThunk<
  AiResponseDetailDto,
  { id: string },
  AsyncThunkConfig
>(
  'aiResponses/getAiResponseDetail',
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      const response = await api.aiResponse.getAiResponseDetailById(id);

      return parseApiPayload(response);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export type UpsertAiResponseFeedbackArgs = {
  id: string;
  value: AiResponseFeedbackInputDtoValueEnum;
  previousFeedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined;
  previousThumbsUpCount: number;
  previousThumbsDownCount: number;
};

export const upsertAiResponseFeedback = createAsyncThunk<
  void,
  UpsertAiResponseFeedbackArgs,
  AsyncThunkConfig
>(
  'aiResponses/upsertAiResponseFeedback',
  async ({ id, value }, { dispatch, rejectWithValue }) => {
    try {
      await api.aiResponseFeedback.upsertAiResponseFeedback(id, { value });
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export type DeleteAiResponseFeedbackArgs = {
  id: string;
  previousFeedback: AiResponseDetailDtoCurrentUserFeedbackEnum | undefined;
  previousThumbsUpCount: number;
  previousThumbsDownCount: number;
};

export const deleteAiResponseFeedback = createAsyncThunk<
  void,
  DeleteAiResponseFeedbackArgs,
  AsyncThunkConfig
>(
  'aiResponses/deleteAiResponseFeedback',
  async ({ id }, { dispatch, rejectWithValue }) => {
    try {
      await api.aiResponseFeedback.deleteAiResponseFeedback(id);
    } catch (error) {
      const apiError = toApiError(error);

      if (!apiError.isAbortedError) {
        dispatch(
          setError({ message: apiError.message, uiMessage: apiError.uiMessage })
        );
      }

      return rejectWithValue(apiError);
    }
  }
);

export const downloadAiResponseContextById = createAsyncThunk<
  void,
  { id: string },
  AsyncThunkConfig
>(
  'aiResponses/downloadAiResponseContextById',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await api.aiResponse.getAiResponseContextFilesById(id, {
        responseType: 'blob',
      });

      const blob = parseApiPayload(response) as Blob;
      const filename = parseDownloadFilename(response, `ai-response-${id}.zip`);

      await downloadBlob(blob, filename);
    } catch (error) {
      const apiError = toApiError(error);

      return rejectWithValue(apiError);
    }
  }
);
